import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';

import { StatValue } from './StatValue';
import MemoryIcon from '@mui/icons-material/Memory';

import * as adminApi from '../api/admin';
import type { PipelineStatus } from '../api/admin';

/**
 * Platform-dashboard insights-pipeline card (DASH-PIPELINE-HEALTH).
 *
 * Headline number is `pending + running` — "work currently in the
 * queue". Subtext carries the operational decorators: oldest
 * pending age, recent failure count. Worker chip strip is one chip
 * per insights-worker replica, colour-coded by heartbeat freshness.
 *
 * Polls every 10 s. The endpoint is cheap (two SELECTs over public
 * tables) so the cadence is safe.
 */

const POLL_INTERVAL_MS = 10_000;

export function PipelineStatusCard({
  size = { xs: 12, md: 6 },
}: {
  /** Grid column span. Defaults to half-width (md: 6). */
  size?: React.ComponentProps<typeof Grid>['size'];
} = {}) {
  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await adminApi.getPipelineStatus();
        if (!cancelled) {
          setStatus(next);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            (err as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? 'failed to load pipeline status',
          );
        }
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  return (
    <Grid size={size} sx={{ display: 'flex' }}>
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <MemoryIcon color="primary" fontSize="small" />
            <Typography variant="overline" color="text.secondary">
              Insights pipeline
            </Typography>
          </Stack>
          {status === null && error === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : status !== null ? (
            <PipelineBody status={status} />
          ) : null}
        </CardContent>
      </Card>
    </Grid>
  );
}

function PipelineBody({ status }: { status: PipelineStatus }) {
  const inFlight = status.queue.pending + status.queue.running;
  const oldest = formatAge(status.oldest_pending_age_seconds);
  return (
    <>
      <StatValue value={inFlight} />
      <Typography variant="body2" color="text.secondary">
        {status.queue.pending} pending · {status.queue.running} running
        {oldest ? ` · oldest ${oldest}` : ''}
        {status.queue.failed_recent > 0
          ? ` · ${status.queue.failed_recent} failed (last hour)`
          : ''}
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1.5 }}
      >
        {status.workers.length === 0 ? (
          <Chip
            label="no workers checked in"
            color="error"
            size="small"
            variant="outlined"
          />
        ) : (
          status.workers.map((w) => (
            <Tooltip
              key={w.instance_id}
              title={`${w.instance_id} — last heartbeat ${formatAge(
                w.age_seconds,
              )} ago${w.version ? ` (${w.version})` : ''}`}
            >
              <Chip
                label={w.instance_id}
                size="small"
                variant="outlined"
                color={w.status === 'ok' ? 'success' : 'warning'}
              />
            </Tooltip>
          ))
        )}
      </Stack>
    </>
  );
}

function formatAge(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined) return null;
  if (seconds < 1) return 'just now';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}
